import type { UseMutationResult } from '@tanstack/react-query'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useApiWrapperWithErrorValidation } from 'shared/hooks/use-api-wrapper-with-error-validation'
import { RequestMethodsEnum } from 'shared/hooks/use-api-wrapper-with-error-validation/types'
import type { PostInterface } from 'modules/community/types/post-interface'
import { communityPostKeys } from './key-factory'

export interface UpdatePostBody {
  title: string
  path: string
  content: string
  topic?: number | null
  attachments?: any[]
}

export const useUpdatePost = ({
  postId,
  onSuccess,
  onError,
}: {
  postId: number
  onSuccess?: () => void
  onError?: (error: Error) => void
}): UseMutationResult<PostInterface, Error, UpdatePostBody> => {
  const queryClient = useQueryClient()

  const { fetcher } = useApiWrapperWithErrorValidation<
    RequestMethodsEnum.put,
    PostInterface,
    UpdatePostBody
  >({
    method: RequestMethodsEnum.put,
  })

  return useMutation({
    mutationFn: body => fetcher(`/api/community/post/${postId}`, body),

    onMutate: async body => {
      await queryClient.cancelQueries({ queryKey: communityPostKeys.all })

      const snapshot = queryClient.getQueriesData({ queryKey: communityPostKeys.all })

      const updateListItem = (items: PostInterface[]): PostInterface[] =>
        items.map(post =>
          post.id === postId
            ? {
                ...post,
                title: body.title,
                path: body.path,
                content: body.content,
                topic: body.topic
                  ? { id: body.topic, path: post.topic.path, name: post.topic.name }
                  : post.topic,
                attachments: body.attachments ?? post.attachments,
              }
            : post,
        )

      queryClient.setQueriesData<{ pages: { items: PostInterface[] }[] }>(
        { queryKey: communityPostKeys.all },
        old => {
          if (!old || !('pages' in old)) return old
          return {
            ...old,
            pages: old.pages.map(page => ({ ...page, items: updateListItem(page.items) })),
          }
        },
      )

      queryClient.setQueriesData<PostInterface[]>(
        { queryKey: communityPostKeys.pinnedLists() },
        old =>
          old?.map(post =>
            post.id === postId
              ? {
                  ...post,
                  title: body.title,
                  path: body.path,
                  content: body.content,
                  topic: body.topic
                    ? { id: body.topic, path: post.topic.path, name: post.topic.name }
                    : post.topic,
                  attachments: body.attachments ?? post.attachments,
                }
              : post,
          ),
      )

      queryClient.setQueriesData<PostInterface>({ queryKey: communityPostKeys.details() }, old => {
        if (old?.id !== postId) return old
        return {
          ...old,
          title: body.title,
          path: body.path,
          content: body.content,
          topic: body.topic
            ? { id: body.topic, path: old.topic.path, name: old.topic.name }
            : old.topic,
          attachments: body.attachments ?? old.attachments,
        }
      })

      return { snapshot }
    },

    onSuccess: onSuccess ? (_data, _vars, _ctx) => onSuccess() : undefined,
    onError: (error, _body, context) => {
      if (context?.snapshot) {
        for (const [key, data] of context.snapshot) {
          queryClient.setQueryData(key, data)
        }
      }
      onError?.(error)
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: communityPostKeys.all })
    },
  })
}
